/**
 * Cross-browser fullscreen helpers for the document root.
 * requestFullscreen requires a user gesture in standard browsers (Chrome will
 * reject and may log if called from timers, effects, or WebSocket handlers).
 * Only call from click / pointer / key handlers.
 */

import { getCurrentEnvironment } from '../config/nakama.config';
import { useAuthStore } from '../domains/auth';

const FULLSCREEN_PREFER_ENTER_KEY = 'ponder-fullscreen-prefer-enter';

/** When set in sessionStorage, match fullscreen gate is disabled for this tab session. */
const FULLSCREEN_ENFORCEMENT_SKIP_KEY = 'ponder-fullscreen-enforcement-skip';

type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  mozFullScreenElement?: Element | null;
  mozCancelFullScreen?: () => Promise<void> | void;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return (
    document.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

/** Whether any element is currently in fullscreen mode. */
export function isFullscreenActive(): boolean {
  return getFullscreenElement() !== null;
}

function voidFullscreenPromise(p: Promise<void> | void): void {
  if (p && typeof (p as Promise<void>).catch === 'function') {
    void (p as Promise<void>).catch(() => {});
  }
}

/**
 * Invoke the native fullscreen API synchronously (no await before the call).
 * Use as the first line in a click/submit handler, before any `await`, so the
 * browser still treats it as user-initiated.
 */
export function kickOffDocumentFullscreenFromGesture(): void {
  if (isFullscreenActive()) return;

  const el = document.documentElement as FullscreenCapableElement;
  try {
    if (typeof el.requestFullscreen === 'function') {
      voidFullscreenPromise(el.requestFullscreen());
      return;
    }
    if (typeof el.webkitRequestFullscreen === 'function') {
      voidFullscreenPromise(el.webkitRequestFullscreen() as Promise<void> | void);
      return;
    }
    if (typeof el.mozRequestFullScreen === 'function') {
      voidFullscreenPromise(el.mozRequestFullScreen() as Promise<void> | void);
      return;
    }
    if (typeof el.msRequestFullscreen === 'function') {
      voidFullscreenPromise(el.msRequestFullscreen() as Promise<void> | void);
    }
  } catch {
    /* sync rejection — ignore */
  }
}

/** Document fullscreen API exists and the browser reports it is allowed (avoids trapping iOS users). */
export function isDocumentFullscreenCapable(): boolean {
  const el = document.documentElement as FullscreenCapableElement;
  const hasApi = !!(
    typeof el.requestFullscreen === 'function' ||
    typeof el.webkitRequestFullscreen === 'function' ||
    typeof el.mozRequestFullScreen === 'function' ||
    typeof el.msRequestFullscreen === 'function'
  );
  if (!hasApi) return false;

  const doc = document as Document & { webkitFullscreenEnabled?: boolean };
  if (typeof document.fullscreenEnabled === 'boolean') {
    if (document.fullscreenEnabled) return true;
    return doc.webkitFullscreenEnabled === true;
  }
  return true;
}

export function isFullscreenEnforcementSkipped(): boolean {
  try {
    return sessionStorage.getItem(FULLSCREEN_ENFORCEMENT_SKIP_KEY) === '1';
  } catch {
    return true;
  }
}

export function skipFullscreenEnforcementForSession(): void {
  try {
    sessionStorage.setItem(FULLSCREEN_ENFORCEMENT_SKIP_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Forgets that the player went on without full screen, as a fresh tab has
 * it: the gate asks again. The gallery's scenes start each on a fresh page's
 * state, so a skip pressed in one is not still pressed in the next.
 */
export function forgetFullscreenEnforcementSkip(): void {
  try {
    sessionStorage.removeItem(FULLSCREEN_ENFORCEMENT_SKIP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Whether join-time fullscreen kickoff and the in-match fullscreen gate apply.
 * Disabled for local Docker/dev (`environment === 'local'`) and for admin hub
 * joins (`enforceMatchFullscreen` cleared in user-select before connect).
 */
export function shouldEnforceMatchFullscreen(): boolean {
  if (getCurrentEnvironment() === 'local') return false;
  return useAuthStore.getState().enforceMatchFullscreen;
}

/**
 * Request fullscreen for the whole document (HTML root).
 * @returns true if the API ran without throwing (may still fail asynchronously on old WebKit).
 */
export async function requestDocumentFullscreen(): Promise<boolean> {
  if (isFullscreenActive()) return true;

  const el = document.documentElement as FullscreenCapableElement;
  try {
    if (typeof el.requestFullscreen === 'function') {
      await el.requestFullscreen();
      return true;
    }
    if (typeof el.webkitRequestFullscreen === 'function') {
      await Promise.resolve(el.webkitRequestFullscreen());
      return true;
    }
    if (typeof el.mozRequestFullScreen === 'function') {
      await Promise.resolve(el.mozRequestFullScreen());
      return true;
    }
    if (typeof el.msRequestFullscreen === 'function') {
      await Promise.resolve(el.msRequestFullscreen());
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** Exit fullscreen if active. */
async function exitDocumentFullscreen(): Promise<void> {
  if (!isFullscreenActive()) return;
  const doc = document as FullscreenDocument;
  try {
    if (typeof document.exitFullscreen === 'function') {
      await document.exitFullscreen();
      return;
    }
    if (typeof doc.webkitExitFullscreen === 'function') {
      await Promise.resolve(doc.webkitExitFullscreen());
      return;
    }
    if (typeof doc.mozCancelFullScreen === 'function') {
      await Promise.resolve(doc.mozCancelFullScreen());
      return;
    }
    if (typeof doc.msExitFullscreen === 'function') {
      await Promise.resolve(doc.msExitFullscreen());
    }
  } catch {
    /* user gesture not required to exit; ignore */
  }
}

/**
 * Ask for full screen — from a click, as the browser requires — and remember
 * the choice, so the next match's start lobby takes the player's first tap
 * back into it (game-screen.tsx): the start lobby's own button, the full-screen
 * gate's Return, and the globe's.
 */
export function enterDocumentFullscreen(): void {
  void requestDocumentFullscreen().then((ok) => {
    if (ok || isFullscreenActive()) rememberFullscreenPreferred();
  });
}

/** Leave full screen if the game fills it, or ask for it as enterDocumentFullscreen does. */
export function toggleDocumentFullscreen(): void {
  if (isFullscreenActive()) {
    void exitDocumentFullscreen();
    return;
  }
  enterDocumentFullscreen();
}

/** The player has chosen full screen before (enterDocumentFullscreen): a start lobby's first tap asks for it again. */
export function getFullscreenPreferEnter(): boolean {
  try {
    return localStorage.getItem(FULLSCREEN_PREFER_ENTER_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberFullscreenPreferred(): void {
  try {
    localStorage.setItem(FULLSCREEN_PREFER_ENTER_KEY, '1');
  } catch {
    /* private mode / quota */
  }
}
